import React, { useEffect, useState } from "react";
import { withStyles } from "@material-ui/styles";
import { withRouter } from "react-router-dom";
import { compose } from "recompose";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography,
} from "@material-ui/core";
import moment from "moment";
import { postChallengeMealLog } from "../../../../apis/challengeService";
import Snackbar from "../../../common/Snackbar";
import Properties from "../../../../properties/Properties";
import dinnerIcon from "../../../../assets/svg-icons/dinner.svg";
import breakfastIcon from "../../../../assets/svg-icons/breakfast.svg";
import lunchIcon from "../../../../assets/svg-icons/lunch.svg";
import miniMeal from "../../../../assets/svg-icons/minimeal.svg";
import ApplicationConstants from "../../../../constants/ApplicationConstants";

function MealResponseComponent({
  classes,
  perDayViewData,
  challengeId,
  style,
  viewDate,
  runnerDetails,
  initialLoad,
}) {
  const perDayMealData = perDayViewData?.dietPlan;

  // 🔹 State for dialogs + structured meal items
  const [mealDialogs, setMealDialogs] = useState({});
  const [mealItems, setMealItems] = useState({});

  // 🔹 Parse mealPlan object → array of items
  const parseMealPlan = (mealPlanObj = {}) =>
    Object.entries(mealPlanObj).map(([key, value]) => ({
      key,
      name: key.replace(/_/g, " "),
      quantity: parseFloat(value) || 0,
    }));

  // 🔹 Convert plant-wise map → usable structure
  const parsePlantWiseMeal = (plantArray = []) =>
    plantArray.map((plant) => ({
      division: plant.division,
      mealPlan: parseMealPlan(plant.mealPlan),
    }));

  useEffect(() => {
    if (!perDayMealData) return;

    const meals = {};

    // 🔹 Breakfast
    if (Array.isArray(perDayMealData.breakfastMap)) {
      meals["Breakfast"] = parsePlantWiseMeal(perDayMealData.breakfastMap);
    }

    // 🔹 Lunch
    if (Array.isArray(perDayMealData.lunchMap)) {
      meals["Lunch"] = parsePlantWiseMeal(perDayMealData.lunchMap);
    }

    // 🔹 Dinner
    if (Array.isArray(perDayMealData.dinnerMap)) {
      meals["Dinner"] = parsePlantWiseMeal(perDayMealData.dinnerMap);
    }

    // 🔹 Other Meals (Mini Meal, Snacks, etc.)
    if (Array.isArray(perDayMealData.otherMealList)) {
      perDayMealData.otherMealList.forEach((m) => {
        meals[m.mealType] = parsePlantWiseMeal(m.mealOptions);
      });
    }

    setMealItems(meals);

    // Initialize dialog states
    const dialogMap = {};
    Object.keys(meals).forEach((mt) => (dialogMap[mt] = false));
    setMealDialogs(dialogMap);
  }, [perDayMealData]);

  // 🔹 Update quantity per plant → per item
  const handleQuantityChange = (mealType, plantIndex, itemIndex, delta) => {
    setMealItems((prev) => ({
      ...prev,
      [mealType]: prev[mealType].map((plant, pIdx) =>
        pIdx === plantIndex
          ? {
              ...plant,
              mealPlan: plant.mealPlan.map((item, iIdx) =>
                iIdx === itemIndex
                  ? {
                      ...item,
                      quantity: Math.max(0, item.quantity + delta),
                    }
                  : item
              ),
            }
          : plant
      ),
    }));
  };

  // 🔹 Build API payload plant-wise
  const handleSubmit = (mealType) => {
    const formattedResponse = mealItems[mealType].map((plant) => ({
      division: plant.division,
      mealPlan: plant.mealPlan.reduce((acc, item) => {
        acc[item.key] = String(item.quantity);
        return acc;
      }, {}),
    }));

    const payload = {
      programChallengeId: Number(challengeId),
      mealType,
      mealResponse: formattedResponse,
      mealDate: viewDate,
      runnerId: runnerDetails?.runnerId,
    };

    console.log("📤 FINAL PAYLOAD:", payload);

    // Replace with your actual API function
    postChallengeMealLog(payload)
      .then((res) => {
        Snackbar.show({
          variant: "success",
          isAutoHide: true,
          message:
            res.data?.success?.verbose || "Meal log submitted successfully!",
        });
        initialLoad(challengeId, viewDate);
        setMealDialogs((prev) => ({ ...prev, [mealType]: false }));
      })
      .catch((err) => {
        Snackbar.show({
          variant: "error",
          isAutoHide: true,
          message: err.data?.error?.verbose || "Something went wrong",
        });
        console.error("❌ Error submitting:", err);
      });
  };

  const getGradientByIndex = (index) =>
  ApplicationConstants.gradientPalette[index % ApplicationConstants.gradientPalette.length];

  return (
    <div className={classes.container} style={style}>
      <div className="dietSection">
        <span className="title">Meal Plan</span>
        <div className={classes.buttonAlignment}>
          {Object.keys(mealItems).map((mealType) => (
            <Button
              key={mealType}
              variant="contained"
              className={classes.mealBtnStyle}
              onClick={() =>
                setMealDialogs((prev) => ({ ...prev, [mealType]: true }))
              }
            >
              <img
                src={
                  mealType === "Breakfast"
                    ? breakfastIcon
                    : mealType === "Lunch"
                    ? lunchIcon
                    : mealType === "Dinner"
                    ? dinnerIcon
                    : miniMeal
                }
                alt={`${mealType} icon`}
                className={classes.iconStyle}
              />
              {mealType}
            </Button>
          ))}
        </div>
      </div>

      {/* 🔹 Dynamic Dialog per MealType */}
      {Object.entries(mealItems).map(([mealType, plants]) => (
        <Dialog
          key={mealType}
          open={mealDialogs[mealType] || false}
          maxWidth="xs"
          fullWidth
          onClose={(event, reason) => {
            if (reason !== "backdropClick") {
              setMealDialogs((prev) => ({ ...prev, [mealType]: false }));
            }
          }}
          disableEscapeKeyDown
          PaperProps={{ style: { borderRadius: 12, padding: 12 } }}
        >
          <DialogContent>
            <Typography
              variant="h6"
              style={{ textAlign: "center", fontWeight: 600, marginBottom: 10 }}
            >
              {mealType}
            </Typography>

            {plants.map((plant, pIndex) => (
              <Box
                key={plant.division}
                mb={2}
                style={{
                  // border: "1px solid #ccc",
                  padding: 10,
                  borderRadius: 8,
                  background: getGradientByIndex(pIndex)
                }}
              >
                <Typography
                  variant="subtitle1"
                  style={{ fontWeight: 700, marginBottom: 6 }}
                >
                  {plant.division}
                </Typography>

                {plant.mealPlan.map((item, iIndex) => (
                  <Box
                    key={item.key}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1}
                  >
                    <Typography style={{ textTransform: "capitalize" }}>
                      {item.name}
                    </Typography>

                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      style={{backgroundColor: "#fff"}}
                      className={classes.container}
                    >
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleQuantityChange(mealType, pIndex, iIndex, -1)
                        }
                      >
                        –
                      </IconButton>
                      <Typography>{item.quantity}</Typography>
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleQuantityChange(mealType, pIndex, iIndex, 1)
                        }
                      >
                        +
                      </IconButton>
                    </Box>
                  </Box>
                ))}
              </Box>
            ))}
          </DialogContent>

          <DialogActions className={classes.buttonAlignmentDialogBox}>
            <Button
              className={classes.btnStyle}
              onClick={() =>
                setMealDialogs((prev) => ({ ...prev, [mealType]: false }))
              }
            >
              Cancel
            </Button>
            <Button
              className={classes.btnStyle}
              color="primary"
              onClick={() => handleSubmit(mealType)}
            >
              Submit
            </Button>
          </DialogActions>
        </Dialog>
      ))}
    </div>
  );
}

const styles = (theme) => ({
  container: {
    display: "flex",
    alignItems: "center",
    gap: "20px",
    borderRadius: "10px",
    boxShadow: "rgba(99, 99, 99, 0.2) 0px 2px 8px 0px",
    padding: "10px",
    [theme.breakpoints.down("sm")]: {
      gap: "10px",
    },
    "& .title": {
      fontWeight: 600,
      fontSize: "22px",
      minWidth: "fit-content",
      [theme.breakpoints.down("sm")]: {
        fontSize: "17px",
      },
    },
    "& .dietSection": {
      color: "black",
      display: "flex",
      flexDirection: "column", // stack items vertically
      alignItems: "flex-start", // optional: aligns items to the start
      [theme.breakpoints.down("xs")]: {
        gap: "0px",
      },
    },
    "& .dietSection p": {
      margin: "4px 0", // reduce margin between lines
    },
  },
  buttonAlignment: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexWrap: "wrap",
    rowGap: "10px",
    margin:"10px 0",
    gap: "10px",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "flex-start",
    },
  },
  buttonAlignmentDialogBox: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "10px",
  },
  btnStyle: {
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#296DFA",
    },
    width: "150px",
    alignContent: "center",
    padding: "6px 16px",
    maxWidth: "300px",
    height: "38px",
    borderRadius: "10px",
    backgroundColor: "#09456C",
    color: "#fff",
    fontWeight: "bold",
    [theme.breakpoints.down("sm")]: {
      width: "50%",
    },
  },
mealBtnStyle: {
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#f0bc7dff",
  },

  width: "auto",     
  minWidth: "auto",  

  padding: "4px 12px", 
  height: "38px",

  borderRadius: "10px",
  backgroundColor: "#FFBA66",
  color: "#09456C",
  fontWeight: "bold",

  display: "flex",
  alignItems: "center",
  justifyContent: "center",
},
  iconStyle: {
    display: "flex",
    justifyContent: "space-around",
    alignItems: "center",
    width: "20px",
    height: "20px",
    marginRight: "10px",
  },
});

export default compose(withStyles(styles), withRouter)(MealResponseComponent);
